import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FileTree from '../FileTree/FileTree';
import styles from './Folder.module.scss';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { FileNode } from '../../../../DataRoom';
import {
    dataRoomArrowUp,
    downloadIconDataRoom,
    downloadDarkGray,
    folderIconDataRoom,
    dataRoomDownloadIcon,
    downloadIconDataRoomHovered,
} from '../../../../../../assets/hostedassets';
import threeDotVertical from '../../../../../../assets/three-dot-vertical.svg';

import useFetchDataRoomFiles from '../../../../useFetchDataRoomFiles';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../../../../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';
import {
    AICA_DATAROOM_FILE_ARCHIVE,
    AICA_DATAROOM_FILE_DELETE,
    AICA_DATAROOM_FOLDER_VIEWED,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { FILE_FOLDER_TYPES } from '../../../../../../store/investor/constants';
import { isLenderType } from '../../../../../../utils/utils';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import type { FeatureVisibility } from '../../../../../../utils/visibilityConfigUtils';
import { computeDropdownLeft, computeDropdownTop } from '../dropdownPositionUtils';

interface FolderProps {
    name: string;
    innerFiles: FileNode[];
    lastUpdated: string;
    index: number;
    parentFolderName: string;
    fetchFiles: Function;
    setShowDataRoomModal: (value: { showModal: boolean; docId?: number; currentDocType?: string }) => void;
    isNew: any;
    selectedItems?: Set<string>;
    onItemSelect?: (itemId: string) => void;
    showCheckboxes?: boolean;
    isFolderFullySelected?: (folderId: string) => boolean;
    currentFileViewType?: string;
    dataRoomDownloadVisibility: FeatureVisibility;
    addToReportsVisible: boolean;
    onSendForSigning?: (docId: string, fileName: string) => void;
    signableExtensions?: string[];
    showSignedCopyVisibility?: FeatureVisibility;
}

const Folder: React.FC<FolderProps> = ({
    name,
    innerFiles,
    lastUpdated,
    index,
    fetchFiles,
    // isRecurCompany,
    parentFolderName,
    setShowDataRoomModal,
    isNew,
    selectedItems = new Set(),
    onItemSelect,
    showCheckboxes = false,
    isFolderFullySelected,
    currentFileViewType,
    dataRoomDownloadVisibility,
    addToReportsVisible,
    onSendForSigning,
    signableExtensions = [],
    showSignedCopyVisibility,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [downloadImgUrl, setDownloadImgUrl] = useState(downloadIconDataRoomHovered);
    const { downloadTheFolder, downloading, handleDeleteFile, getFormattedDate, DeleteFolderButton, ArchiveFolderButton } =
        useFetchDataRoomFiles(fetchFiles);
    const [masterFolderName, setMasterFolderName] = useState('');
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const threeDotsRef = useRef<HTMLSpanElement | null>(null);
    const fileViewType = searchParams.get('fileViewType');
    const isLender = isLenderType();
    const isArchiveView = fileViewType === 'archived';
    // MCA records are read-only sweep documents: no archiving from the UI, so they never leave the tab.
    const isMcaRecordsView = fileViewType === 'mcaRecords';
    let foldertoDelete = parentFolderName ? parentFolderName : name;
    const user = useAppSelector((state) => state.user.userData);
    const selectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const isPartner = user?.accountType === USER_TYPE.PARTNER;
    const isLenderOrPartner = isLender || isPartner;
    const folderId = name; // Use name as folder ID since folders don't have docId
    const isSelected = isFolderFullySelected ? isFolderFullySelected(folderId) : false;

    useEffect(() => {
        if (index === 1) {
            setMasterFolderName(name);
        } else {
            setMasterFolderName(parentFolderName ?? name);
        }
    }, [parentFolderName, index, name]);

    const calculateDropdownPosition = () => {
        if (!threeDotsRef.current) return;
        const rect = threeDotsRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current?.getBoundingClientRect();
        const dropdownHeight = dropdownRect?.height ?? 0;
        const dropdownWidth = dropdownRect?.width ?? 0;
        setDropdownPosition({
            top: computeDropdownTop(rect, dropdownHeight),
            left: computeDropdownLeft(rect, dropdownWidth),
        });
    };

    // useLayoutEffect (not useEffect): recalculates before the browser paints, so the
    // menu never flashes at its stale/initial position before snapping to the correct one.
    useLayoutEffect(() => {
        if (showOtherOptions) {
            calculateDropdownPosition();
        }
    }, [showOtherOptions]);

    useEffect(() => {
        const handleScroll = () => {
            if (showOtherOptions) {
                setShowOtherOptions(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showOtherOptions]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                threeDotsRef.current &&
                !threeDotsRef.current.contains(event.target as Node)
            ) {
                setShowOtherOptions(false);
            }
        };
        if (showOtherOptions) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOtherOptions]);

    const dropdown =
        showOtherOptions &&
        createPortal(
            <div ref={dropdownRef} className={styles.OtherOptions} style={dropdownPosition}>
                {isArchiveView ? (
                    <>
                        <DeleteFolderButton
                            innerFiles={innerFiles}
                            name={name}
                            foldertoDelete={foldertoDelete}
                            parentFolderName={parentFolderName}
                            fetchFiles={fetchFiles}
                            setShowOtherOptions={setShowOtherOptions}
                            user={user}
                            selectedCompany={selectedCompany}
                            handleDeleteFile={handleDeleteFile}
                        />
                    </>
                ) : (
                    <>
                        <DeleteFolderButton
                            innerFiles={innerFiles}
                            name={name}
                            foldertoDelete={foldertoDelete}
                            parentFolderName={parentFolderName}
                            fetchFiles={fetchFiles}
                            setShowOtherOptions={setShowOtherOptions}
                            user={user}
                            selectedCompany={selectedCompany}
                            handleDeleteFile={handleDeleteFile}
                        />
                        {!isMcaRecordsView && (
                            <ArchiveFolderButton
                                innerFiles={innerFiles}
                                name={name}
                                foldertoDelete={foldertoDelete}
                                parentFolderName={parentFolderName}
                                fetchFiles={fetchFiles}
                                setShowOtherOptions={setShowOtherOptions}
                                user={user}
                                selectedCompany={selectedCompany}
                                handleDeleteFile={handleDeleteFile}
                            />
                        )}
                    </>
                )}
            </div>,
            document.body,
        );

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (onItemSelect) {
            onItemSelect(folderId);
        }
    };

    return (
        <div className={styles.folder}>
            <div
                className={isLenderOrPartner ? styles.folderHeaderLenderView : styles.folderHeader}
                onClick={() => {
                    if (!isOpen) {
                        Mixpanel.track(AICA_DATAROOM_FOLDER_VIEWED, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: selectedCompany?.assesseeOrgId,
                            folderName: name,
                        });
                    }
                    setIsOpen(!isOpen);
                }}
            >
                <div className={styles.nameCell}>
                    <div
                        style={{
                            // -1: the row now carries its own 1.25rem left padding (added to align
                            // Status/Data From/Data Till/Last Updated with the header), which already
                            // accounts for one indent level — this offsets so top-level (index=1) rows
                            // land exactly under the header checkbox instead of double-indenting.
                            marginLeft: `${(index - 1) * 1.25}rem`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <span>
                            <img className={isOpen ? styles.arrowDown : styles.arrowUp} src={dataRoomArrowUp} alt={'arrow'} />
                        </span>
                        {showCheckboxes && (
                            <div className={styles.checkboxCell} onClick={handleCheckboxClick}>
                                <input type="checkbox" checked={isSelected} onChange={handleCheckboxChange} className={styles.checkbox} />
                            </div>
                        )}
                        <span>
                            <img src={folderIconDataRoom} alt={'file'} />
                        </span>
                        <span className={styles.FolderName}>{name}</span>
                    </div>
                </div>
                <span className={isNew ? styles.labelStatus : ''}>{isNew ? 'New' : ''}</span>
                <span>{getFormattedDate('folder', '')}</span>
                <span>{getFormattedDate('folder', '')}</span>
                <span className={styles.FolderDate}>{lastUpdated}</span>
                <span />
                {!isLenderOrPartner && dataRoomDownloadVisibility.show && (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            if (dataRoomDownloadVisibility.disabled) return;
                            downloadTheFolder(innerFiles, name, false);
                        }}
                        className={styles.downloadIconContainer}
                        style={dataRoomDownloadVisibility.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        onMouseEnter={() => !dataRoomDownloadVisibility.disabled && setDownloadImgUrl(downloadIconDataRoomHovered)}
                        onMouseLeave={() => !dataRoomDownloadVisibility.disabled && setDownloadImgUrl(downloadIconDataRoomHovered)}
                    >
                        {downloading ? <LoadingSpinner color="#6021B3" /> : <img src={downloadImgUrl} alt={'icon'} />}
                    </span>
                )}
                {/* {!isLenderOrPartner && <div className={styles.separator} />} */}
                {!isLenderOrPartner && (
                    <span
                        ref={threeDotsRef}
                        className={styles.ThreeDots}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowOtherOptions((prev) => !prev);
                        }}
                    >
                        <img src={threeDotVertical} style={{ cursor: 'pointer', width: '30px', height: '30px' }} alt={'More'} />
                    </span>
                )}
                {dropdown}
            </div>
            <div className={`${styles.folderContent} ${isOpen ? styles.open : styles.close}`}>
                <FileTree
                    data={innerFiles}
                    index={index}
                    masterFolderName={masterFolderName}
                    fetchFiles={fetchFiles}
                    setShowDataRoomModal={setShowDataRoomModal}
                    selectedItems={selectedItems}
                    onItemSelect={onItemSelect}
                    showCheckboxes={showCheckboxes}
                    isFolderFullySelected={isFolderFullySelected}
                    fileViewType={currentFileViewType}
                    dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                    addToReportsVisible={addToReportsVisible}
                    onSendForSigning={onSendForSigning}
                    signableExtensions={signableExtensions}
                    showSignedCopyVisibility={showSignedCopyVisibility}
                />
            </div>
        </div>
    );
};

export default Folder;
